/**
 * UploadDocumentsDialog - pick a project, then drop or browse for documents
 * and upload them to that project's knowledge files
 * (POST /projects/{project_id}/knowledge-files).
 *
 * The project list comes from ActiveProjectContext, which already fetches it
 * when the app starts. It is only queried again here if that fetch failed or
 * came back empty.
 */
import { useEffect, useRef, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUploadOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useDialogStyles } from '../dialogs/dialogStyles';
import { useActiveProject } from '../activeProject/ActiveProjectContext';
import { uploadKnowledgeFile } from '../../api/knowledgeFiles';
import { ApiError } from '../../utils/apiClient';
import { canPerform } from '../../roles';
import {
  checkFile,
  formatBytes,
  inferTypeTag,
  type FileRuleViolation,
} from './uploadFileRules';

export type UploadedSummary = { count: number; projectName: string };

type Props = {
  open: boolean;
  onClose: () => void;
  /** Called once, after a run in which every pending file uploaded. */
  onUploaded?: (summary: UploadedSummary) => void;
};

type ItemStatus = 'invalid' | 'queued' | 'uploading' | 'done' | 'failed';

type UploadItem = {
  id: number;
  file: File;
  status: ItemStatus;
  violation?: FileRuleViolation;
  /** Why this file failed to upload, already worded for the user. */
  failure?: string;
};

const useStyles = makeStyles(theme => ({
  dropzone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(4, 3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    '&:hover, &:focus-visible': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
      outline: 'none',
    },
  },
  dropzoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  dropzoneDisabled: { opacity: 0.5, pointerEvents: 'none' },
  dropzoneHint: { fontSize: '0.74rem', color: theme.palette.text.secondary },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius * 1.5,
    border: `1px solid ${theme.palette.divider}`,
  },
  fileMain: { flex: 1, minWidth: 0 },
  fileName: {
    fontSize: '0.85rem',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileMeta: { fontSize: '0.74rem', color: theme.palette.text.secondary },
  fileError: { fontSize: '0.74rem', color: theme.palette.error.main },
  doneIcon: { color: theme.palette.success.main },
  notice: {
    marginTop: theme.spacing(1),
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  noticeError: {
    marginTop: theme.spacing(1),
    fontSize: '0.8rem',
    color: theme.palette.error.main,
  },
}));

const fileKey = (file: File) =>
  `${file.name}:${file.size}:${file.lastModified}`;

type Translate = (key: string) => string;

function describeFailure(err: unknown, t: Translate): string | undefined {
  if (err instanceof ApiError) {
    if (err.status === 403) return t('uploadDialog.errorForbidden');
    if (err.status === 413) return t('uploadDialog.errorTooLargeForServer');
    if (err.code === 'REQUEST_TIMEOUT') return t('uploadDialog.errorTimeout');
    if (err.code === 'NETWORK_ERROR') return t('uploadDialog.errorNetwork');
  }
  // Anything else is the backend's own wording (e.g. a 500 naming a missing
  // config). Users get the generic message; the detail goes to the console.
  return undefined;
}

// A project the user may upload into - the same rule ProjectFilesPanel uses.
const canUploadTo = (role: Parameters<typeof canPerform>[0]) =>
  canPerform(role, 'project.artifacts.manage');

export function UploadDocumentsDialog({ open, onClose, onUploaded }: Props) {
  const c = useDialogStyles();
  const classes = useStyles();
  const { t } = useTranslation('knowledgeBase');
  const { projects, status, activeProjectId, reload } = useActiveProject();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const nextIdRef = useRef(1);

  const [pickedProjectId, setPickedProjectId] = useState<number | null>(null);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Fall back to the project the user is already working in.
  const candidateId = pickedProjectId ?? activeProjectId;
  const projectId =
    candidateId !== null &&
    projects.some(p => p.id === candidateId && canUploadTo(p.role))
      ? candidateId
      : null;
  const hasUploadableProject = projects.some(p => canUploadTo(p.role));

  // No list yet (the app-start fetch failed or found nothing): ask again each
  // time the dialog opens. Keyed on `open` only - reload() flips the status
  // back through 'loading', which would otherwise re-run this in a loop.
  useEffect(() => {
    if (open && (status === 'error' || status === 'empty')) reload();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => {
    setPickedProjectId(null);
    setItems([]);
    setIsDragOver(false);
    setUploading(false);
    dragCounterRef.current = 0;
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const addFiles = (files: FileList | File[]) => {
    // The upload loop works from a snapshot, so a file added now would be
    // dropped when the dialog closes after a successful run.
    if (uploading) return;
    const known = new Set(items.map(item => fileKey(item.file)));
    const fresh = Array.from(files).filter(file => {
      const key = fileKey(file);
      if (known.has(key)) return false;
      known.add(key);
      return true;
    });
    const added: UploadItem[] = fresh.map(file => {
      const violation = checkFile(file);
      return {
        id: nextIdRef.current++,
        file,
        status: violation ? 'invalid' : 'queued',
        violation,
      };
    });
    if (added.length > 0) setItems(current => [...current, ...added]);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) addFiles(event.target.files);
    // Allow picking the same file again after removing it.
    event.target.value = '';
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    dragCounterRef.current += 1;
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    addFiles(event.dataTransfer.files);
  };

  const openFilePicker = () => {
    if (!uploading) fileInputRef.current?.click();
  };

  const removeItem = (id: number) =>
    setItems(current => current.filter(item => item.id !== id));

  const setItemStatus = (id: number, next: ItemStatus, failure?: string) =>
    setItems(current =>
      current.map(item =>
        item.id === id ? { ...item, status: next, failure } : item,
      ),
    );

  const handleUpload = async () => {
    if (projectId === null) return;
    const pending = items.filter(
      item => item.status === 'queued' || item.status === 'failed',
    );
    if (pending.length === 0) return;

    setUploading(true);
    let anyFailed = false;
    // Files that went through on an earlier attempt still count toward the total.
    let uploadedCount = items.filter(item => item.status === 'done').length;
    // One at a time: each file travels base64-encoded in a single JSON body.
    for (const item of pending) {
      setItemStatus(item.id, 'uploading');
      try {
        // eslint-disable-next-line no-await-in-loop
        await uploadKnowledgeFile(
          projectId,
          item.file,
          inferTypeTag(item.file.name),
        );
        setItemStatus(item.id, 'done');
        uploadedCount += 1;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Knowledge file upload failed', err);
        anyFailed = true;
        setItemStatus(item.id, 'failed', describeFailure(err, t));
      }
    }
    setUploading(false);

    if (!anyFailed) {
      const projectName = projects.find(p => p.id === projectId)?.name ?? '';
      reset();
      onClose();
      onUploaded?.({ count: uploadedCount, projectName });
    }
  };

  const violationText = (violation?: FileRuleViolation) => {
    if (violation === 'tooLarge') return t('uploadDialog.errorFileTooLarge');
    if (violation === 'invalidType') return t('uploadDialog.errorInvalidType');
    return undefined;
  };

  const uploadableCount = items.filter(
    item => item.status === 'queued' || item.status === 'failed',
  ).length;
  const canSubmit = projectId !== null && uploadableCount > 0 && !uploading;

  let projectNotice: string | undefined;
  let projectNoticeIsError = false;
  if (status === 'loading') {
    projectNotice = t('uploadDialog.projectsLoading');
  } else if (status === 'error') {
    projectNotice = t('uploadDialog.projectsLoadError');
    projectNoticeIsError = true;
  } else if (projects.length === 0) {
    projectNotice = t('uploadDialog.projectsEmpty');
  } else if (!hasUploadableProject) {
    projectNotice = t('uploadDialog.projectsNoUploadAccess');
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={c.title}>
        <span className={c.titleTextWrap}>
          <span className={c.titleText}>{t('uploadDialog.title')}</span>
        </span>
        <IconButton
          size="small"
          className={c.closeBtn}
          aria-label={t('dialogs.close')}
          disabled={uploading}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={c.body}>
        <div className={c.field}>
          <label className={c.label} htmlFor="upload-docs-project">
            {t('uploadDialog.project')}
          </label>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            id="upload-docs-project"
            value={projectId ?? ''}
            disabled={uploading || projects.length === 0}
            onChange={event => setPickedProjectId(Number(event.target.value))}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="" disabled>
              {t('uploadDialog.projectPlaceholder')}
            </MenuItem>
            {projects.map(project => (
              <MenuItem
                key={project.id}
                value={project.id}
                disabled={!canUploadTo(project.role)}
              >
                {project.name}
              </MenuItem>
            ))}
          </TextField>
          {projectNotice && (
            <div
              role={projectNoticeIsError ? 'alert' : undefined}
              className={
                projectNoticeIsError ? classes.noticeError : classes.notice
              }
            >
              {projectNotice}
            </div>
          )}
        </div>

        <div className={c.field}>
          <div className={c.label}>{t('uploadDialog.documents')}</div>
          <div
            className={`${classes.dropzone} ${
              isDragOver ? classes.dropzoneActive : ''
            } ${uploading ? classes.dropzoneDisabled : ''}`}
            role="button"
            tabIndex={uploading ? -1 : 0}
            aria-disabled={uploading}
            aria-label={t('uploadDialog.dropzoneLabel')}
            onClick={openFilePicker}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openFilePicker();
              }
            }}
            onDragOver={event => event.preventDefault()}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CloudUploadIcon />
            <div>{t('uploadDialog.dropzoneLabel')}</div>
            <div className={classes.dropzoneHint}>
              {t('uploadDialog.dropzoneHint')}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handleFileChange}
            />
          </div>

          {items.length > 0 && (
            <div className={classes.fileList}>
              {items.map(item => (
                <div key={item.id} className={classes.fileRow}>
                  <div className={classes.fileMain}>
                    <div className={classes.fileName} title={item.file.name}>
                      {item.file.name}
                    </div>
                    {item.status === 'invalid' && (
                      <div className={classes.fileError}>
                        {violationText(item.violation)}
                      </div>
                    )}
                    {item.status === 'failed' && (
                      <div className={classes.fileError}>
                        {t('uploadDialog.uploadError')}
                        {item.failure && ` (${item.failure})`}
                      </div>
                    )}
                    {item.status !== 'invalid' && item.status !== 'failed' && (
                      <div className={classes.fileMeta}>
                        {formatBytes(item.file.size)}
                      </div>
                    )}
                  </div>
                  {item.status === 'uploading' && (
                    <CircularProgress
                      size={18}
                      aria-label={t('uploadDialog.uploading')}
                    />
                  )}
                  {item.status === 'done' && (
                    <CheckCircleIcon
                      fontSize="small"
                      className={classes.doneIcon}
                      titleAccess={t('uploadDialog.uploaded')}
                    />
                  )}
                  {item.status !== 'uploading' && item.status !== 'done' && (
                    <IconButton
                      size="small"
                      disabled={uploading}
                      aria-label={t('uploadDialog.removeFile', {
                        name: item.file.name,
                      })}
                      onClick={() => removeItem(item.id)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions className={c.actions}>
        <Button onClick={handleClose} disabled={uploading}>
          {t('dialogs.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          onClick={handleUpload}
        >
          {uploading ? t('uploadDialog.uploading') : t('uploadDialog.upload')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
